/*
 * SelahRest — a global context that renders a 60-second post-capture
 * reflection overlay on demand.
 *
 * Usage from any page:
 *   const { startRest } = useSelahRest();
 *   startRest();                       // 60-second default
 *   startRest({ duration: 45 });       // shorter pause
 *   startRest({ onComplete: () => …}); // callback when the timer ends
 *
 * The overlay obeys the visual spec:
 *   – Screen fades to #121212 over 1.5s.
 *   – A central "breathing orb" cycles inhale (4s, +40% scale → #D2B48C)
 *     → hold (2s) → exhale (4s → #D4AF37).
 *   – Text "Pause. Reflect. Rest in His Presence." (#FDFBF7, Playfair)
 *     pulses with the breath.
 *   – A subtle low-opacity "Skip Pause" text link appears after 15s.
 *   – Local ambient audio (procedurally generated pad — no external asset
 *     required) fades in over 2s and fades out over 3s.
 *
 * We deliberately don't ship a big MP3 file. Instead we use the Web Audio
 * API to synthesise a soft ambient pad (three sine tones + slow LFO) —
 * this keeps the PWA install size small and works offline. The synth is
 * built and torn down cleanly per session.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const COLORS = {
  bg: '#121212',
  gold: '#D4AF37',
  sand: '#D2B48C',
  alabaster: '#FDFBF7',
  fgMuted: 'rgba(253, 251, 247, 0.42)',
  fgSubtle: 'rgba(253, 251, 247, 0.72)',
};

const DEFAULT_DURATION_S = 60;
const SKIP_APPEARS_AFTER_S = 15;

// ─── Ambient pad synthesis ─────────────────────────────────────────
// Web Audio API — three detuned sines forming a warm chord, gently
// amplitude-modulated by a low-frequency oscillator. No external asset
// needed, works offline, no Google/Apple content dependencies.
function createAmbientPad() {
  if (typeof window === 'undefined') return null;
  const AudioCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtor) return null;

  let ctx;
  try {
    ctx = new AudioCtor();
  } catch (_) {
    return null;
  }

  const master = ctx.createGain();
  master.gain.value = 0.0001; // start silent
  master.connect(ctx.destination);

  // Soft low-pass filter for a "pad" feel
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 700;
  filter.Q.value = 0.6;
  filter.connect(master);

  // Chord: G3 (196Hz), D4 (293.66Hz), Bb4 (466.16Hz) — a gentle, restful voicing
  const freqs = [196, 293.66, 466.16];
  const oscs = freqs.map((f) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = f;
    const g = ctx.createGain();
    g.gain.value = 0.25;
    osc.connect(g);
    g.connect(filter);
    return { osc, gain: g };
  });

  // Slow LFO to breathe the volume subtly (~0.1Hz)
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.1;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.06;
  lfo.connect(lfoGain);
  lfoGain.connect(master.gain);

  oscs.forEach(({ osc }) => osc.start());
  lfo.start();

  const stop = () => {
    try {
      oscs.forEach(({ osc }) => osc.stop());
      lfo.stop();
    } catch (_) {
      /* ignore */
    }
    try {
      ctx.close();
    } catch (_) {
      /* ignore */
    }
  };

  return {
    fadeIn: (durationSec = 2) => {
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      master.gain.linearRampToValueAtTime(0.18, now + durationSec);
    },
    fadeOut: (durationSec = 3) =>
      new Promise((resolve) => {
        const now = ctx.currentTime;
        master.gain.cancelScheduledValues(now);
        master.gain.setValueAtTime(master.gain.value, now);
        master.gain.linearRampToValueAtTime(0.0001, now + durationSec);
        setTimeout(() => {
          stop();
          resolve();
        }, durationSec * 1000 + 100);
      }),
    stop,
  };
}

// ─── React Context ────────────────────────────────────────────────
const SelahRestContext = createContext(null);

export function useSelahRest() {
  const ctx = useContext(SelahRestContext);
  if (!ctx) {
    throw new Error('useSelahRest must be used within <SelahRestProvider>');
  }
  return ctx;
}

export function SelahRestProvider({ children }) {
  const [active, setActive] = useState(false);
  const [duration, setDuration] = useState(DEFAULT_DURATION_S);
  const [remaining, setRemaining] = useState(DEFAULT_DURATION_S);
  const onCompleteRef = useRef(null);
  const tickRef = useRef(null);
  const padRef = useRef(null);
  const respectMotionRef = useRef(false);

  // Respect prefers-reduced-motion — if the user has that on, we still show
  // the overlay but skip the scaling animation.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    try {
      respectMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (_) {
      respectMotionRef.current = false;
    }
  }, []);

  const stopInternal = useCallback(async () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (padRef.current) {
      try {
        await padRef.current.fadeOut(3);
      } catch (_) {
        try {
          padRef.current.stop();
        } catch (_) {}
      }
      padRef.current = null;
    }
    setActive(false);
  }, []);

  const skipRest = useCallback(async () => {
    await stopInternal();
    if (onCompleteRef.current) {
      const cb = onCompleteRef.current;
      onCompleteRef.current = null;
      try {
        cb({ skipped: true });
      } catch (_) {}
    }
  }, [stopInternal]);

  const completeRest = useCallback(async () => {
    await stopInternal();
    if (onCompleteRef.current) {
      const cb = onCompleteRef.current;
      onCompleteRef.current = null;
      try {
        cb({ skipped: false });
      } catch (_) {}
    }
  }, [stopInternal]);

  const startRest = useCallback(
    (opts = {}) => {
      const d = Math.max(15, Number(opts.duration) || DEFAULT_DURATION_S);
      onCompleteRef.current = typeof opts.onComplete === 'function' ? opts.onComplete : null;
      setDuration(d);
      setRemaining(d);
      setActive(true);

      // Kick off ambient pad (best-effort — audio may be blocked until user gesture)
      try {
        const pad = createAmbientPad();
        if (pad) {
          padRef.current = pad;
          // Small delay to let AudioContext resume if user hasn't interacted
          setTimeout(() => {
            try {
              pad.fadeIn(2);
            } catch (_) {}
          }, 150);
        }
      } catch (_) {
        /* audio is optional */
      }

      // Countdown tick
      if (tickRef.current) clearInterval(tickRef.current);
      const startedAt = Date.now();
      tickRef.current = setInterval(() => {
        const elapsed = (Date.now() - startedAt) / 1000;
        const left = Math.max(0, d - elapsed);
        setRemaining(left);
        if (left <= 0) {
          if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
          }
          completeRest();
        }
      }, 200);
    },
    [completeRest]
  );

  // Lock body scroll while overlay is open
  useEffect(() => {
    if (!active) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [active]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (padRef.current) {
        try {
          padRef.current.stop();
        } catch (_) {}
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      startRest,
      skipRest,
      isActive: active,
      remainingSeconds: remaining,
    }),
    [startRest, skipRest, active, remaining]
  );

  const showSkip = duration - remaining >= SKIP_APPEARS_AFTER_S;
  const reducedMotion = respectMotionRef.current;

  return (
    <SelahRestContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {active && (
          <motion.div
            key="selah-rest-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Selah Rest"
            data-testid="selah-rest-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: 'easeInOut' }}
            className="fixed inset-0 z-[300] flex flex-col items-center justify-center"
            style={{ background: COLORS.bg }}
            onKeyDown={(e) => {
              // Trap keyboard: allow Esc to skip (only after skip visible)
              if (e.key === 'Escape' && showSkip) skipRest();
            }}
          >
            {/* Ambient noise texture */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
              }}
            />

            {/* Breathing orb */}
            <BreathingOrb reducedMotion={reducedMotion} />

            {/* Instructional copy — pulses with the breathing cycle */}
            <motion.p
              key="selah-rest-mantra"
              initial={{ opacity: 0, y: 10 }}
              animate={
                reducedMotion
                  ? { opacity: 0.85, y: 0 }
                  : { opacity: [0.6, 0.95, 0.6], y: 0 }
              }
              transition={
                reducedMotion
                  ? { duration: 0.8 }
                  : { duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }
              }
              className="relative mt-12 text-center text-[19px] sm:text-[22px] font-medium tracking-wide px-8"
              style={{
                color: COLORS.alabaster,
                fontFamily: '"Playfair Display", serif',
                letterSpacing: '0.01em',
              }}
              data-testid="selah-rest-mantra"
            >
              Pause. Reflect. Rest in His Presence.
            </motion.p>

            {/* Sub-caption */}
            <p
              className="relative mt-4 text-[12.5px]"
              style={{ color: COLORS.fgMuted, fontFamily: '"Crimson Text", serif' }}
            >
              Selah.
            </p>

            {/* Timer ring (subtle) */}
            <RestTimerRing
              remaining={remaining}
              duration={duration}
              className="relative mt-16"
            />

            {/* Skip Pause — subtle, delayed */}
            <AnimatePresence>
              {showSkip && (
                <motion.button
                  key="skip-pause"
                  type="button"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.55 }}
                  whileHover={{ opacity: 0.9 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  onClick={skipRest}
                  data-testid="selah-rest-skip-button"
                  className="absolute bottom-8 right-8 text-[11.5px] tracking-wide underline-offset-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(212,175,55,0.5)] rounded-md px-2 py-1"
                  style={{ color: COLORS.fgMuted }}
                >
                  Skip pause
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </SelahRestContext.Provider>
  );
}

// ─── The orb itself ────────────────────────────────────────────────
function BreathingOrb({ reducedMotion }) {
  // Inhale 4s (scale up 40%, colour → sand)
  // Hold  2s (steady)
  // Exhale 4s (scale down, colour → gold)
  // Total cycle = 10s
  const keyframes = reducedMotion
    ? { scale: 1, backgroundColor: COLORS.gold }
    : {
        scale: [1, 1.4, 1.4, 1],
        backgroundColor: [COLORS.gold, COLORS.sand, COLORS.sand, COLORS.gold],
      };
  const timings = reducedMotion
    ? { duration: 0.6 }
    : { duration: 10, times: [0, 0.4, 0.6, 1], repeat: Infinity, ease: 'easeInOut' };

  return (
    <div className="relative flex items-center justify-center">
      {/* Outer soft glow (breathes on its own) */}
      {!reducedMotion && (
        <motion.div
          aria-hidden
          className="absolute rounded-full"
          style={{
            width: 240,
            height: 240,
            background:
              'radial-gradient(circle, rgba(212,175,55,0.28) 0%, rgba(212,175,55,0.06) 55%, rgba(0,0,0,0) 80%)',
            filter: 'blur(12px)',
          }}
          animate={{ scale: [0.9, 1.15, 1.15, 0.9], opacity: [0.6, 0.9, 0.9, 0.6] }}
          transition={{ duration: 10, times: [0, 0.4, 0.6, 1], repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      {/* Main orb */}
      <motion.div
        data-testid="selah-rest-orb"
        className="relative rounded-full"
        style={{
          width: 120,
          height: 120,
          boxShadow:
            '0 0 40px rgba(212,175,55,0.35), inset 0 0 30px rgba(253,251,247,0.15)',
          filter: 'blur(0.5px)',
        }}
        animate={keyframes}
        transition={timings}
      />
    </div>
  );
}

// ─── Ambient timer ring ────────────────────────────────────────────
function RestTimerRing({ remaining, duration, className }) {
  const size = 68;
  const stroke = 2;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, remaining / duration));
  const offset = c * (1 - pct);
  const seconds = Math.max(0, Math.ceil(remaining));

  return (
    <div
      className={className}
      style={{ width: size, height: size, position: 'relative' }}
      aria-label={`${seconds} seconds remaining`}
      data-testid="selah-rest-timer"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rotate-[-90deg]">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(253,251,247,0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={COLORS.gold}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 250ms linear' }}
        />
      </svg>
      <div
        className="absolute inset-0 flex items-center justify-center text-[13px]"
        style={{
          color: COLORS.fgSubtle,
          fontFamily: '"Playfair Display", serif',
        }}
      >
        {seconds}s
      </div>
    </div>
  );
}
